import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { WorkOrderAssignmentStatus } from "@/gql/graphql";
import {
  markCompletedWorkOrderAssignment,
  markDeliveredWorkOrderAssignment,
  markPickedUpWorkOrderAssignment,
  markReceiptsUploadedWorkOrderAssignment,
  resetWorkOrderAssignment,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderDriverMutations";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { ApproveReceiptsDialog } from "@/app/ProDrivers/WorkOrders/Components/Dialogs/ApproveReceiptsDialog";

export function useUpdateAssignmentStatus(id: string): {
  handleStatusChange: (status: WorkOrderAssignmentStatus) => Promise<void>;
  isPending: boolean;
} {
  const { mutateAsync: reset, isPending: isResetting } = useGqlMutation(
    resetWorkOrderAssignment,
  );
  const { mutateAsync: markPickedUp, isPending: isMarkingPickedUp } =
    useGqlMutation(markPickedUpWorkOrderAssignment);
  const { mutateAsync: markDelivered, isPending: isMarkingDelivered } =
    useGqlMutation(markDeliveredWorkOrderAssignment);
  const { mutateAsync: markReceiptsUploaded, isPending: isMarkingReceipts } =
    useGqlMutation(markReceiptsUploadedWorkOrderAssignment);
  const { mutateAsync: markCompleted, isPending: isMarkingCompleted } =
    useGqlMutation(markCompletedWorkOrderAssignment);
  const { openAsPromise: openApproveReceiptsDialog } = useDialog(
    ApproveReceiptsDialog,
  );

  const handleStatusChange = async (status: WorkOrderAssignmentStatus) => {
    switch (status) {
      case WorkOrderAssignmentStatus.Assigned:
        await reset({ id });
        return;
      case WorkOrderAssignmentStatus.OnRoad:
        await markPickedUp({ id });
        return;
      case WorkOrderAssignmentStatus.Delivered:
        await markDelivered({ id });
        return;
      case WorkOrderAssignmentStatus.ReceiptsUploaded:
        await markReceiptsUploaded({ id });
        return;
      case WorkOrderAssignmentStatus.ReceiptsApproved:
        try {
          await openApproveReceiptsDialog({ workOrderAssignmentId: id });
        } catch {
          // User cancelled the dialog
        }
        return;
      case WorkOrderAssignmentStatus.Completed:
        await markCompleted({ id });
        return;
    }
  };

  const isPending =
    isResetting ||
    isMarkingPickedUp ||
    isMarkingDelivered ||
    isMarkingReceipts ||
    isMarkingCompleted;

  return { handleStatusChange, isPending };
}
